'use client';

import { useEffect, useRef } from 'react';

import { useBehaviorSubject } from '@ps/functions/hooks/useBehaviorSubject';

import { useChatContext } from '../hooks/useChatContext';
import { useChatMessageContext } from '../hooks/useChatMessages';
import { ChatMessageBubble } from './ChatMessageBubble';

interface Props {
  /**
   * Extra bottom space, in px, for when this list is the LAST element in the
   * room — i.e. a read-only Group (promotional) session, where `ChatRoom`
   * renders no `ChatInputBar` below it and the mobile bottom nav would overlap
   * the final message. `undefined` in a Private session, where the input bar
   * sits underneath and owns the clearance itself.
   */
  bottomInset?: number;
}

/**
 * Scrollable message list for a single chat room. Messages live in
 * the `messages$` BehaviorSubject newest-first (matches the legacy
 * insertion order), so we render them reversed and auto-scroll to the
 * bottom whenever a new one lands.
 */
export function ChatMessageList({ bottomInset }: Props = {}) {
  const { currentUserId } = useChatContext();
  const { messages$, status$ } = useChatMessageContext();
  const [messages] = useBehaviorSubject(messages$);
  const [status] = useBehaviorSubject(status$);

  const scrollRef = useRef<HTMLDivElement>(null);

  // Keep the latest message in view. We anchor on the count rather
  // than the array identity so repeated patches of the same message
  // (e.g. send-success after sending) don't re-scroll.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  if (status.isLoading && messages.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <span className="text-content-3 text-xs">Loading…</span>
      </div>
    );
  }

  if (status.error && messages.length === 0) {
    return (
      <p
        role="alert"
        className="bg-danger/10 m-4 rounded-md border border-red-500/30 px-4 py-3 text-sm text-red-200"
      >
        Couldn’t load messages.{' '}
        {status.error instanceof Error ? status.error.message : ''}
      </p>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-1 px-6 text-center">
        <p className="text-content-1 text-sm">No messages yet</p>
        <p className="text-content-3 text-xs">
          Say hello to start the conversation.
        </p>
      </div>
    );
  }

  return (
    // `[container-type:inline-size]` lets media-card bubbles size
    // themselves against the message-list width via `cqw` units —
    // matches the PopshopRN `useMessageMaxWidth` rule of
    // `min(315px, 55% of list width)`.
    <div
      ref={scrollRef}
      className="flex flex-1 flex-col overflow-y-auto px-4 [container-type:inline-size]"
    >
      {/* `bottomInset` is applied to the CONTENT, not the scroller, so the
          room's background still runs to the viewport bottom and the floating
          nav overlays it with no visible band. */}
      <div
        className="flex flex-col-reverse gap-2 py-4"
        style={bottomInset == null ? undefined : { paddingBottom: bottomInset }}
      >
        {messages.map(message => (
          <ChatMessageBubble
            key={`${message.clientId}-${message.messageId}`}
            message={message}
            currentUserId={currentUserId}
          />
        ))}
      </div>
    </div>
  );
}
